/**
 * Desktop only: below `lg` the page scrolls natively.
 */
export const usePdpPaneScroll = (
  panesSelector = '[data-pdp-gallery-scroll], [data-pdp-info-scroll]'
) => {
  const { breakpoint } = useProjectBreakpoint()
  const isLarge = breakpoint.greaterOrEqual('lg')
  const { stripsHeight } = useHeaderStripsHeight()

  const paneFrom = (target: EventTarget | null) =>
    target instanceof Element
      ? (target.closest(panesSelector) as HTMLElement | null)
      : null

  const isScrollContainer = (pane: HTMLElement) => {
    const { overflowY } = getComputedStyle(pane)
    return (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      pane.scrollHeight - pane.clientHeight > 1
    )
  }

  const hasRoom = (pane: HTMLElement, deltaY: number) => {
    const max = pane.scrollHeight - pane.clientHeight
    return deltaY > 0 ? pane.scrollTop < max - 1 : pane.scrollTop > 1
  }

  let snapping = false
  // Decided on the first event of a gesture and kept for its inertia events,
  // which the browser marks as not cancelable.
  let paneOwnsGesture = false

  const snapPastStrips = () => {
    if (snapping) return
    snapping = true
    window.scrollTo({ top: stripsHeight.value, behavior: 'smooth' })
    setTimeout(() => (snapping = false), 400)
  }

  const aboveStrips = () => window.scrollY < stripsHeight.value - 1

  useEventListener(
    window,
    'wheel',
    (event: WheelEvent) => {
      if (!isLarge.value || !event.deltaY) return

      const pane = paneFrom(event.target)
      if (!pane) {
        paneOwnsGesture = false
        return
      }

      if (event.deltaY > 0 && aboveStrips()) {
        paneOwnsGesture = false
        event.preventDefault()
        snapPastStrips()
        return
      }

      if (event.cancelable) paneOwnsGesture = isScrollContainer(pane)
      if (!paneOwnsGesture || hasRoom(pane, event.deltaY)) return

      if (event.cancelable) event.preventDefault()
      window.scrollBy({ top: event.deltaY })
    },
    { passive: false }
  )

  let touchStartY = 0

  useEventListener(
    window,
    'touchstart',
    (event: TouchEvent) => {
      touchStartY = event.touches[0]?.clientY ?? 0
    },
    { passive: true }
  )

  useEventListener(
    window,
    'touchmove',
    (event: TouchEvent) => {
      if (!isLarge.value) return
      const currentY = event.touches[0]?.clientY ?? 0
      if (touchStartY - currentY <= 0) return
      if (!aboveStrips() || !paneFrom(event.target)) return

      event.preventDefault()
      snapPastStrips()
    },
    { passive: false }
  )
}
